import { useState } from "react";
import { MessageCircle, Phone, Leaf, Zap, Sparkles, Check } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Classes from "../components/Classes";
import BookingDialog from "../components/BookingDialog";
import { Toaster } from "../components/ui/sonner";
import { useScrollReveal } from "../lib/useScrollReveal";
import { BRAND } from "../lib/brand";

const WA_LINK = "https://wa.me/919867413477";
const TEL_LINK = "tel:+919867413477";

/* ---------- Page Hero ---------- */
const PageHero = () => {
  const ref = useScrollReveal();
  return (
    <section
      ref={ref}
      data-testid="classes-hero"
      className="relative pt-36 pb-20 md:pt-44 md:pb-28 bg-gradient-to-b from-[#F1ECE0] via-[#F9F6F0] to-[#F9F6F0]"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
        <p className="reveal text-[11px] tracking-[0.35em] uppercase text-[#D4A24C] mb-5 font-medium">
          Our Classes
        </p>
        <h1 className="reveal font-serif-display text-5xl sm:text-6xl md:text-7xl text-[#153326] font-light leading-[1.05]">
          Find Your <em className="italic">Perfect</em> Class
        </h1>
        <p className="reveal mt-6 text-[#2C5F4A]/80 text-base md:text-lg font-light max-w-xl mx-auto">
          Every level, every format — guided with personal attention.
        </p>
      </div>
    </section>
  );
};

/* ---------- Section 1: Levels (custom for this page) ---------- */
const LEVEL_DATA = [
  {
    tag: "Level 01",
    title: "Beginners",
    subtitle:
      "No prior experience needed. Start from scratch — safely and confidently.",
    headerBg: "#1F4A38",
    covered: [
      "Foundational asanas with alignment correction",
      "Basic breathing techniques (pranayama)",
      "Flexibility and posture improvement",
      "Stress relief and relaxation practices",
      "Diet and lifestyle guidance",
    ],
    bestFor: "New students, homemakers, seniors, post-pregnancy recovery",
  },
  {
    tag: "Level 02",
    title: "Intermediate",
    subtitle:
      "Deepen your practice with advanced techniques and therapeutic programs.",
    headerBg: "#153326",
    covered: [
      "Advanced asana sequences and flow work",
      "Strength, mobility and core conditioning",
      "Weight management structured plan",
      "Meditation and mindfulness deepening",
      "Therapeutic programs for holistic wellness",
    ],
    bestFor: "Existing practitioners, weight loss goals, lifestyle transformation",
  },
];

const LevelSection = () => {
  const ref = useScrollReveal();
  return (
    <section
      ref={ref}
      data-testid="page-levels"
      className="py-20 md:py-28 bg-[#F9F6F0]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="reveal text-[11px] tracking-[0.35em] uppercase text-[#D4A24C] mb-5 font-medium">
            Progressive Path
          </p>
          <h2 className="reveal font-serif-display text-4xl sm:text-5xl md:text-6xl text-[#153326] font-light">
            Choose Your <em className="italic">Level</em>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {LEVEL_DATA.map((lvl, i) => (
            <article
              key={lvl.title}
              data-testid={`page-level-${i + 1}`}
              className="reveal rounded-[28px] overflow-hidden bg-white shadow-[0_14px_46px_rgba(31,74,56,0.08)] hover:shadow-[0_22px_60px_rgba(31,74,56,0.14)] transition-all duration-500 hover:-translate-y-1 flex flex-col"
            >
              <div
                className="p-8 md:p-10 relative grain"
                style={{ backgroundColor: lvl.headerBg, color: "#F9F6F0" }}
              >
                <span className="inline-block px-3 py-1 rounded-full bg-white/15 border border-white/20 text-[11px] tracking-[0.25em] uppercase mb-5">
                  {lvl.tag}
                </span>
                <h3 className="font-serif-display text-4xl md:text-5xl font-medium">
                  {lvl.title}
                </h3>
                <p className="mt-4 text-[#F9F6F0]/85 max-w-md leading-relaxed">
                  {lvl.subtitle}
                </p>
              </div>
              <div className="p-8 md:p-10 flex-1 flex flex-col">
                <p className="text-[11px] tracking-[0.3em] uppercase text-[#D4A24C] font-semibold mb-5">
                  What's Covered
                </p>
                <ul className="space-y-4">
                  {lvl.covered.map((c) => (
                    <li key={c} className="flex items-start gap-3">
                      <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#1F4A38]/10 text-[#1F4A38] shrink-0">
                        <Check size={13} strokeWidth={2.5} />
                      </span>
                      <span className="text-[#153326]">{c}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-auto pt-8 border-t border-[#1F4A38]/10 mt-8">
                  <p className="text-sm text-[#2C5F4A]">
                    <span className="text-[#2C5F4A]/70">Best for: </span>
                    <span className="font-semibold text-[#153326]">{lvl.bestFor}</span>
                  </p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Section 2: What We Teach ---------- */
const STYLES = [
  {
    title: "Hatha Yoga",
    Icon: Leaf,
    desc:
      "The foundation of all yoga. Slow, mindful, and deeply restorative. Perfect for alignment and breath awareness.",
    tag: "All Levels",
    featured: false,
  },
  {
    title: "Power Yoga",
    Icon: Zap,
    desc:
      "A dynamic, strength-focused practice that builds endurance, tones the body and sharpens the mind.",
    tag: "Most Popular",
    featured: true,
  },
  {
    title: "Yoga for Beginners",
    Icon: Sparkles,
    desc:
      "A welcoming, gentle introduction to yoga — focused on comfort, correct form, and building a lasting habit.",
    tag: "Beginner Friendly",
    featured: false,
  },
];

const WhatWeTeach = () => {
  const ref = useScrollReveal();
  return (
    <section
      ref={ref}
      data-testid="what-we-teach"
      className="py-20 md:py-28 bg-gradient-to-b from-[#F9F6F0] to-[#F1ECE0]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14 md:mb-16">
          <p className="reveal text-[11px] tracking-[0.35em] uppercase text-[#D4A24C] mb-5 font-medium">
            Styles
          </p>
          <h2 className="reveal font-serif-display text-4xl sm:text-5xl md:text-6xl text-[#153326] font-light">
            What We <em className="italic">Teach</em>
          </h2>
          <p className="reveal mt-5 text-[#2C5F4A]/80 font-light">
            Styles covered across all our formats.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7 items-stretch">
          {STYLES.map(({ title, Icon, desc, tag, featured }) => (
            <article
              key={title}
              data-testid={`style-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
              className={`reveal rounded-[28px] p-9 md:p-10 flex flex-col h-full transition-all duration-500 ease-out hover:-translate-y-2 ${
                featured
                  ? "bg-[#1F4A38] text-[#F9F6F0] shadow-[0_18px_50px_rgba(31,74,56,0.28)] hover:shadow-[0_26px_70px_rgba(31,74,56,0.4)] border border-[#D4A24C]/30 md:scale-[1.02] md:-my-2"
                  : "bg-white/75 backdrop-blur-xl border border-[#1F4A38]/10 shadow-[0_12px_40px_rgba(31,74,56,0.08)] hover:shadow-[0_22px_60px_rgba(31,74,56,0.14)]"
              }`}
            >
              {featured && (
                <div className="absolute self-end -mt-3 text-[10px] tracking-[0.3em] uppercase text-[#D4A24C]">
                  Featured
                </div>
              )}
              <div
                className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${
                  featured ? "bg-[#D4A24C]/15 text-[#D4A24C]" : "bg-[#1F4A38]/10 text-[#1F4A38]"
                }`}
              >
                <Icon size={26} strokeWidth={1.5} />
              </div>
              <h3
                className={`font-serif-display text-3xl font-medium ${
                  featured ? "text-white" : "text-[#153326]"
                }`}
              >
                {title}
              </h3>
              <p
                className={`mt-4 leading-relaxed flex-1 ${
                  featured ? "text-[#F9F6F0]/90" : "text-[#2C5F4A]"
                }`}
              >
                {desc}
              </p>
              <div className="mt-7">
                <span
                  className={`inline-block text-[11px] tracking-[0.25em] uppercase rounded-full px-3 py-1.5 ${
                    featured
                      ? "bg-[#D4A24C] text-[#153326] font-semibold"
                      : "bg-[#1F4A38]/8 text-[#2C5F4A] border border-[#1F4A38]/10"
                  }`}
                >
                  {tag}
                </span>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Section 4: Session Plans ---------- */
const PLANS = [
  {
    sessions: "12 Sessions",
    schedule: "Monday · Wednesday · Friday",
    cadence: "3 days a week",
    featured: false,
  },
  {
    sessions: "20 Sessions",
    schedule: "Monday to Friday",
    cadence: "5 days a week · Full month",
    featured: true,
    badge: "Most Popular",
  },
  {
    sessions: "16 Sessions",
    schedule: "Monday to Friday (Wednesday off)",
    cadence: "4 days a week",
    featured: false,
  },
];

const SessionPlans = () => {
  const ref = useScrollReveal();
  return (
    <section
      ref={ref}
      data-testid="session-plans"
      className="py-20 md:py-28 bg-[#F9F6F0]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14 md:mb-16">
          <p className="reveal text-[11px] tracking-[0.35em] uppercase text-[#D4A24C] mb-5 font-medium">
            Membership
          </p>
          <h2 className="reveal font-serif-display text-4xl sm:text-5xl md:text-6xl text-[#153326] font-light">
            Choose Your <em className="italic">Plan</em>
          </h2>
          <p className="reveal mt-5 text-[#2C5F4A]/80 font-light">
            Reach out for fee details — we'll find the right plan for you.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7 items-stretch">
          {PLANS.map((p, i) => (
            <article
              key={p.sessions}
              data-testid={`plan-card-${i + 1}`}
              className={`reveal relative rounded-[28px] p-9 md:p-10 flex flex-col text-center transition-all duration-500 ease-out hover:-translate-y-2 ${
                p.featured
                  ? "bg-[#1F4A38] text-[#F9F6F0] shadow-[0_18px_50px_rgba(31,74,56,0.28)] hover:shadow-[0_26px_70px_rgba(31,74,56,0.4)] border border-[#D4A24C]/30 md:scale-[1.03] md:-my-2"
                  : "bg-white/75 backdrop-blur-xl border border-[#1F4A38]/10 shadow-[0_12px_40px_rgba(31,74,56,0.08)] hover:shadow-[0_22px_60px_rgba(31,74,56,0.14)]"
              }`}
            >
              {p.featured && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4A24C] text-[#153326] text-[10px] tracking-[0.3em] uppercase px-4 py-1 rounded-full font-semibold shadow">
                  {p.badge}
                </span>
              )}
              <p
                className={`text-[11px] tracking-[0.35em] uppercase mb-4 ${
                  p.featured ? "text-[#EAC98A]" : "text-[#D4A24C]"
                }`}
              >
                {p.cadence}
              </p>
              <h3
                className={`font-serif-display text-4xl md:text-5xl font-medium ${
                  p.featured ? "text-white" : "text-[#153326]"
                }`}
              >
                {p.sessions}
              </h3>
              <div
                className={`my-7 h-px ${p.featured ? "bg-white/15" : "bg-[#1F4A38]/10"}`}
              />
              <p
                className={`text-[15px] mb-2 ${
                  p.featured ? "text-[#F9F6F0]/90" : "text-[#2C5F4A]"
                }`}
              >
                {p.schedule}
              </p>
              <div className="mt-auto pt-8">
                <a
                  data-testid={`plan-enquire-${i + 1}`}
                  href={WA_LINK}
                  target="_blank"
                  rel="noreferrer"
                  className={`block w-full rounded-full px-6 py-3.5 text-sm font-semibold tracking-wide transition-all duration-300 hover:shadow-lg ${
                    p.featured
                      ? "bg-[#D4A24C] hover:bg-[#C29344] text-[#153326]"
                      : "bg-[#1F4A38] hover:bg-[#153326] text-[#F9F6F0]"
                  }`}
                >
                  Enquire Now
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Section 5: Fee Enquiry Banner ---------- */
const FeeBanner = () => {
  const ref = useScrollReveal();
  return (
    <section
      ref={ref}
      data-testid="fee-banner"
      className="relative overflow-hidden bg-[#1F4A38] text-[#F9F6F0]"
    >
      <div className="grain absolute inset-0" />
      <div className="relative max-w-4xl mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
        <p className="reveal text-[11px] tracking-[0.35em] uppercase text-[#D4A24C] mb-6 font-medium">
          Pricing &amp; Fee Details
        </p>
        <h2 className="reveal font-serif-display text-4xl sm:text-5xl md:text-6xl leading-[1.05] font-light">
          For Fee &amp; <em className="italic text-[#EAC98A]">More</em> Details
        </h2>
        <p className="reveal mt-7 max-w-xl mx-auto text-[#F9F6F0]/85 font-light text-base md:text-lg">
          Connect with us directly — we'll walk you through the right plan for your goals.
        </p>

        <div className="reveal mt-10 flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href={WA_LINK}
            target="_blank"
            rel="noreferrer"
            data-testid="fee-whatsapp-btn"
            className="inline-flex items-center justify-center gap-2 bg-[#D4A24C] hover:bg-[#C29344] text-[#153326] rounded-full px-8 py-4 text-sm font-semibold tracking-wide shadow-[0_18px_50px_rgba(212,162,76,0.35)] hover:shadow-[0_24px_60px_rgba(212,162,76,0.55)] transition-all duration-400 hover:-translate-y-0.5"
          >
            <MessageCircle size={18} strokeWidth={1.8} />
            WhatsApp Us
          </a>
          <a
            href={TEL_LINK}
            data-testid="fee-call-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 text-white/95 hover:bg-white hover:text-[#1F4A38] px-8 py-4 text-sm font-medium tracking-wide transition-all duration-400 backdrop-blur-sm"
          >
            <Phone size={18} strokeWidth={1.8} />
            Call Us
          </a>
        </div>

        <p className="reveal mt-7 text-sm text-[#F9F6F0]/70 tracking-wide">
          {BRAND.contact.phone} · Available Mon – Sun
        </p>
      </div>
    </section>
  );
};

/* ---------- Page Composition ---------- */
const ClassesPage = () => {
  const [bookingOpen, setBookingOpen] = useState(false);
  const openBooking = () => setBookingOpen(true);

  return (
    <div className="App">
      <Header onBook={openBooking} />
      <main>
        <PageHero />
        <LevelSection />
        <WhatWeTeach />
        <Classes waLink={WA_LINK} />
        <SessionPlans />
        <FeeBanner />
      </main>
      <Footer />
      <BookingDialog open={bookingOpen} onOpenChange={setBookingOpen} />
      <Toaster position="top-center" richColors />
    </div>
  );
};

export default ClassesPage;
